import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Button, Spinner, Card, Badge } from "react-bootstrap";
import axios from "axios";
import { getAPIUrl } from "../api/apiUtils";
import { FaSearch, FaComments, FaThumbsUp, FaEye, FaTimes } from "react-icons/fa";
import "./PostsSearch.scss";

const API_BASE_URL = getAPIUrl();
const RESULTS_PER_PAGE = 100;

const SOURCES = [
  { id: "vk", title: "ВК" },
  { id: "tg", title: "Telegram" },
  { id: "news", title: "Новости" },
  { id: "youtube", title: "YouTube" },
];

const RESULT_LABELS = {
  post: "Пост",
  comment: "Комментарий",
  news: "Новость",
  video: "Видео",
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getHighlightTerms = (query) => {
  const normalizedQuery = String(query || "").trim();
  if (!normalizedQuery) return [];
  const terms = new Set();
  terms.add(normalizedQuery);
  normalizedQuery
    .replace(/[.,;:!?()[\]{}"«»]+/g, " ")
    .split(/\s+/)
    .map((term) => term.trim())
    .filter((term) => term.length >= 2)
    .forEach((term) => terms.add(term));
  return [...terms].sort((a, b) => b.length - a.length);
};

const getApproximateHighlightStems = (query) => {
  const endings =
    /(иями|ями|ами|ого|его|ому|ему|ыми|ими|иях|ьях|ах|ях|ий|ый|ой|ая|ое|ые|ие|ую|юю|ом|ем|ам|ям|ов|ев|ей|ия|ья|ью|ии|ьи|а|я|ы|и|е|у|ю|о|ь)$/i;

  return getHighlightTerms(query)
    .filter((term) => !/\s/.test(term) && /[А-Яа-яЁё]/.test(term))
    .map((term) => term.toLocaleLowerCase("ru-RU").replace(endings, ""))
    .filter((term) => term.length >= 4)
    .filter((term, index, list) => list.indexOf(term) === index)
    .sort((a, b) => b.length - a.length);
};

const toDisplayText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
};

const countValue = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") return Number(value) || 0;
  if (Array.isArray(value)) return value.length;
  if (value && typeof value === "object") {
    if (typeof value.count === "number") return value.count;
    if (typeof value.total === "number") return value.total;
    if (Array.isArray(value.items)) return value.items.length;
  }
  return 0;
};

const textFromComment = (comment) =>
  toDisplayText(
    comment?.text ||
      comment?.message ||
      comment?.comment ||
      comment?.text_original ||
      comment?.text_display ||
      comment?.description
  );

const likesFromComment = (comment) => countValue(comment?.likes ?? comment?.like_count);

const PostsSearch = () => {
  const [source, setSource] = useState("vk");
  const [channels, setChannels] = useState([]);
  const [selectedChannelIds, setSelectedChannelIds] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedPosts, setExpandedPosts] = useState({});
  const [postComments, setPostComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});

  const selectedChannels = useMemo(
    () => channels.filter((channel) => selectedChannelIds.includes(channel.id)),
    [channels, selectedChannelIds]
  );

  const channelTitleById = useMemo(() => {
    return channels.reduce((acc, channel) => {
      acc[channel.id] = channel.title || channel.id;
      return acc;
    }, {});
  }, [channels]);

  const allSelected = channels.length > 0 && selectedChannelIds.length === channels.length;

  const loadChannels = useCallback(async (nextSource) => {
    setLoadingChannels(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/source_data/channels`, {
        params: {
          source: nextSource,
          include_unlisted: nextSource === "vk" ? "false" : undefined,
        },
      });
      const nextChannels = response.data?.channels || [];
      setChannels(nextChannels);
      setSelectedChannelIds([]);
    } catch (requestError) {
      console.error("Ошибка загрузки источников:", requestError);
      setChannels([]);
      setSelectedChannelIds([]);
      setError("Не удалось загрузить источники для поиска");
    } finally {
      setLoadingChannels(false);
    }
  }, []);

  useEffect(() => {
    loadChannels(source);
  }, [loadChannels, source]);

  const handleSourceChange = (nextSource) => {
    setSource(nextSource);
    setResults([]);
    setTotalResults(0);
    setCurrentPage(1);
    setHasMore(false);
    setPostComments({});
    setExpandedPosts({});
    setError(null);
  };

  const toggleChannel = (channelId) => {
    setSelectedChannelIds((current) =>
      current.includes(channelId)
        ? current.filter((id) => id !== channelId)
        : [...current, channelId]
    );
  };

  const toggleAllChannels = () => {
    setSelectedChannelIds(allSelected ? [] : channels.map((channel) => channel.id));
  };

  const selectAllChannels = () => {
    setSelectedChannelIds(channels.map((channel) => channel.id));
  };

  const clearChannels = () => {
    setSelectedChannelIds([]);
  };

  const removeChannel = (channelId) => {
    setSelectedChannelIds((current) => current.filter((id) => id !== channelId));
  };

  const performSearch = useCallback(async (page = 1, append = false) => {
    if (!searchQuery.trim()) {
      setError("Введите текст для поиска");
      setResults([]);
      setTotalResults(0);
      setCurrentPage(1);
      setHasMore(false);
      return;
    }

    if (selectedChannelIds.length === 0) {
      setError("Выберите хотя бы один источник");
      setResults([]);
      setTotalResults(0);
      setCurrentPage(1);
      setHasMore(false);
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setPostComments({});
      setExpandedPosts({});
    }
    setError(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/search/source/${source}/fulltext`, {
        params: {
          q: searchQuery,
          page,
          limit: RESULTS_PER_PAGE,
          channels: selectedChannelIds.join(","),
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        },
      });
      const nextResults = response.data?.items || [];
      setResults((current) => (append ? [...current, ...nextResults] : nextResults));
      setTotalResults((current) => (append ? current + nextResults.length : nextResults.length));
      setCurrentPage(page);
      setHasMore(Boolean(response.data?.has_more));
    } catch (err) {
      console.error("Ошибка поиска:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Произошла ошибка при поиске"
      );
      if (!append) {
        setResults([]);
        setTotalResults(0);
        setCurrentPage(1);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchQuery, selectedChannelIds, source, startDate, endDate]);

  const handleSearch = (e) => {
    e?.preventDefault();
    performSearch(1, false);
  };

  const loadMoreResults = () => {
    if (!hasMore || loading || loadingMore) return;
    performSearch(currentPage + 1, true);
  };

  const highlightText = (text, query) => {
    const terms = getHighlightTerms(query);
    if (!terms.length || !text) return text;
    const pattern = terms.map(escapeRegExp).join("|");
    const matcher = new RegExp(`(${pattern})`, "gi");
    const exactMatcher = new RegExp(`^(?:${pattern})$`, "i");
    const approximateStems = getApproximateHighlightStems(query);
    const wordMatcher = /([A-Za-zА-Яа-яЁё0-9_@#-]+)/g;
    const highlightApproximate = (value, keyPrefix) => {
      if (!approximateStems.length) return value;
      return String(value).split(wordMatcher).map((part, index) => {
        const normalizedPart = part.toLocaleLowerCase("ru-RU");
        const shouldHighlight = approximateStems.some((stem) => normalizedPart.startsWith(stem));
        return shouldHighlight ? <mark key={`${keyPrefix}-${part}-${index}`}>{part}</mark> : part;
      });
    };
    const parts = String(text).split(matcher);
    return parts.map((part, index) =>
      exactMatcher.test(part)
        ? <mark key={`${part}-${index}`}>{part}</mark>
        : highlightApproximate(part, index)
    );
  };

  const getSearchPreview = (text, query, maxLength = 300) => {
    const value = String(text || "");
    if (value.length <= maxLength) return value;

    const lowerValue = value.toLowerCase();
    const matchNeedles = [...getHighlightTerms(query), ...getApproximateHighlightStems(query)];
    const matchIndex = matchNeedles.reduce((bestIndex, term) => {
      const index = lowerValue.indexOf(term.toLowerCase());
      if (index === -1) return bestIndex;
      return bestIndex === -1 ? index : Math.min(bestIndex, index);
    }, -1);

    if (matchIndex === -1) {
      return `${value.substring(0, maxLength)}...`;
    }

    const contextBefore = 90;
    const start = Math.max(0, matchIndex - contextBefore);
    const end = Math.min(value.length, start + maxLength);
    const prefix = start > 0 ? "..." : "";
    const suffix = end < value.length ? "..." : "";
    return `${prefix}${value.substring(start, end)}${suffix}`;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = typeof value === "number" ? new Date(value * 1000) : new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("ru-RU");
  };

  const resultKey = (result, index) => {
    const data = result.data || {};
    return `${data.source || source}:${result.type}:${data.channel_id}:${data.id}:${index}`;
  };

  const togglePostText = (key) => {
    setExpandedPosts((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const canLoadComments = (result) => {
    const data = result.data || {};
    return result.type !== "comment" && Number(data.comments_count || 0) > 0 && data.channel_id && data.id;
  };

  const togglePostComments = async (result, key) => {
    const data = result.data || {};
    if (postComments[key]) {
      setPostComments((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
      return;
    }

    try {
      setLoadingComments((prev) => ({ ...prev, [key]: true }));
      const response = await axios.get(`${API_BASE_URL}/source_data/comments`, {
        params: {
          source: data.source || source,
          channel_id: data.channel_id,
          post_id: data.id,
          page: 1,
          limit: 50,
        },
      });
      setPostComments((prev) => ({
        ...prev,
        [key]: response.data?.comments || [],
      }));
    } catch (requestError) {
      console.error("Ошибка загрузки комментариев:", requestError);
    } finally {
      setLoadingComments((prev) => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div className="top-level-page-container">
      <div className="page-content">
        <div className="posts-search-container">
          <h2 className="mb-4">
            <FaSearch className="me-2" />
            Поиск по собранным данным
          </h2>

          <Card className="search-filters-card mb-4">
            <Card.Body>
              <Form onSubmit={handleSearch}>
                <div className="source-tabs mb-3" aria-label="Источник данных">
                  {SOURCES.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`source-tab ${source === item.id ? "source-tab--active" : ""}`}
                      onClick={() => handleSourceChange(item.id)}
                      disabled={loading}
                    >
                      {item.title}
                    </button>
                  ))}
                </div>

                <div className="filters-row mb-3">
                  <div className="filter-item group-selector-wrapper">
                    <button
                      type="button"
                      className="group-selector-toggle"
                      onClick={toggleAllChannels}
                      disabled={loadingChannels || loading || !channels.length}
                    >
                      <div className="group-selector-toggle__text">
                        <span className="group-selector-toggle__label">Источники</span>
                        <span className="group-selector-toggle__value">
                          {loadingChannels
                            ? "Загрузка..."
                            : selectedChannels.length
                              ? `Выбрано: ${selectedChannels.length}`
                              : "Выберите источники"}
                        </span>
                      </div>
                    </button>
                  </div>

                  <label className="filter-item date-input-wrapper">
                    <span>С</span>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      aria-label="Начальная дата"
                    />
                  </label>

                  <label className="filter-item date-input-wrapper">
                    <span>По</span>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate}
                      aria-label="Конечная дата"
                    />
                  </label>

                  <div className="filter-item search-button-wrapper">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={loading || loadingChannels || !searchQuery.trim() || selectedChannelIds.length === 0}
                      className="search-button w-100"
                    >
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Поиск...
                        </>
                      ) : (
                        <>
                          <FaSearch className="me-2" />
                          Найти
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="channels-actions">
                  <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    onClick={selectAllChannels}
                    disabled={loadingChannels || loading || !channels.length || allSelected}
                  >
                    Выбрать все
                  </Button>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={clearChannels}
                    disabled={loadingChannels || loading || selectedChannelIds.length === 0}
                  >
                    Снять все
                  </Button>
                </div>

                <div className="channels-picker">
                  {loadingChannels ? <div className="channels-picker__empty">Загрузка источников...</div> : null}
                  {!loadingChannels && !channels.length ? <div className="channels-picker__empty">Источники не найдены</div> : null}
                  {!loadingChannels && channels.length ? (
                    channels.map((channel) => {
                      const checked = selectedChannelIds.includes(channel.id);
                      return (
                        <label key={channel.id} className={`channels-picker__item ${checked ? "channels-picker__item--checked" : ""}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleChannel(channel.id)}
                            disabled={loading}
                          />
                          <span>
                            <strong>{channel.title || channel.id}</strong>
                            {channel.subtitle ? <small>{channel.subtitle}</small> : null}
                          </span>
                        </label>
                      );
                    })
                  ) : null}
                </div>

                {selectedChannels.length > 0 && (
                  <div className="selected-groups-display">
                    <div className="selected-groups-label">
                      Выбрано источников: {selectedChannels.length}
                    </div>
                    <div className="selected-groups-badges">
                      {selectedChannels.map((channel) => (
                        <Badge key={channel.id} bg="primary" className="selected-group-badge">
                          {channel.title || channel.id}
                          <button
                            type="button"
                            className="badge-remove-btn"
                            onClick={() => removeChannel(channel.id)}
                            aria-label="Удалить"
                          >
                            <FaTimes />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="search-textarea-wrapper">
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Введите ключевые слова для поиска..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-textarea"
                  />
                </div>
              </Form>
            </Card.Body>
          </Card>

          <div className="results-panel">
            {error && (
              <div className="alert alert-danger" role="alert">
                Ошибка: {error}
              </div>
            )}

            {!loading && results.length > 0 && (
              <div className="results-info mb-3">
                <p className="text-muted">
                  Найдено результатов: <strong>{totalResults}</strong>
                </p>
              </div>
            )}

            {loading && (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">Выполняется поиск...</p>
              </div>
            )}

            {!loading && results.length === 0 && (searchQuery || selectedChannelIds.length > 0) && (
              <div className="text-center py-5">
                <p className="text-muted">Результаты не найдены</p>
              </div>
            )}

            {!loading && results.length === 0 && !searchQuery && selectedChannelIds.length === 0 && (
              <div className="text-center py-5">
                <p className="text-muted">Введите параметры поиска и нажмите "Найти"</p>
              </div>
            )}

            {!loading && results.length > 0 && (
              <div className="results-list">
                {results.map((result, index) => {
                  const data = result.data || {};
                  const key = resultKey(result, index);
                  const likesValue =
                    typeof data.likes === "number"
                      ? data.likes
                      : data.likes?.count || 0;
                  const viewsValue =
                    typeof data.views === "number"
                      ? data.views
                      : data.views?.count || 0;
                  const commentsCount = Number(data.comments_count || 0);
                  const title = data.title || data.source_title || channelTitleById[data.channel_id] || data.channel_id;
                  const bodyText = data.text || "";
                  const isExpanded = expandedPosts[key] || bodyText.length < 300;
                  const previewText = getSearchPreview(bodyText, searchQuery);

                  return (
                    <Card key={key} className="mb-3 result-card">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <Badge bg={result.type === "comment" ? "secondary" : "primary"} className="me-2">
                              {RESULT_LABELS[result.type] || result.type}
                            </Badge>
                            <span className="text-muted small">
                              {SOURCES.find((item) => item.id === data.source)?.title || data.source} · {title}
                            </span>
                          </div>
                          <div className="text-end">
                            <small className="text-muted me-2">
                              <FaThumbsUp className="me-1" />
                              {likesValue}
                            </small>
                            <small className="text-muted me-2">
                              <FaEye className="me-1" />
                              {viewsValue}
                            </small>
                            <small className="text-muted">
                              <FaComments className="me-1" />
                              {commentsCount}
                            </small>
                          </div>
                        </div>

                        <div className="mb-2">
                          <small className="text-muted">
                            Дата: {formatDate(data.display_date || data.date)}
                          </small>
                          {data.url ? (
                            <a
                              href={data.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ms-3"
                            >
                              Открыть
                            </a>
                          ) : null}
                        </div>

                        {data.title && result.type !== "comment" ? (
                          <h5 className="result-title">{highlightText(data.title, searchQuery)}</h5>
                        ) : null}

                        <div className="result-text">
                          {isExpanded ? (
                            <p>{highlightText(bodyText, searchQuery)}</p>
                          ) : (
                            <>
                              <p>{highlightText(previewText, searchQuery)}</p>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => togglePostText(key)}
                                className="p-0"
                              >
                                Показать полностью
                              </Button>
                            </>
                          )}
                        </div>

                        {canLoadComments(result) ? (
                          <div className="mt-2">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => togglePostComments(result, key)}
                              disabled={loadingComments[key]}
                            >
                              {postComments[key] ? "Скрыть комментарии" : "Показать комментарии"}
                            </Button>

                            {loadingComments[key] && (
                              <div className="mt-2">
                                <Spinner animation="border" size="sm" />{" "}
                                <span className="text-muted">Загрузка комментариев...</span>
                              </div>
                            )}

                            {postComments[key] && (
                              <div className="mt-3">
                                {postComments[key].length === 0 ? (
                                  <p className="text-muted mb-0">Комментарии отсутствуют</p>
                                ) : (
                                  postComments[key].map((comment, commentIndex) => (
                                    <div key={`${key}-comment-${commentIndex}`} className="border-top pt-2 mt-2 small">
                                      <p className="mb-1">{highlightText(textFromComment(comment), searchQuery)}</p>
                                      <div className="text-muted">
                                        <span className="me-2">
                                          Лайки: {likesFromComment(comment)}
                                        </span>
                                        {(comment.formatted_date || comment.date || comment.published_at) && (
                                          <span className="me-2">
                                            {formatDate(comment.formatted_date || comment.date || comment.published_at)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            )}

            {!loading && results.length > 0 && hasMore ? (
              <div className="load-more-results">
                <Button
                  type="button"
                  variant="outline-primary"
                  onClick={loadMoreResults}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Загрузка...
                    </>
                  ) : (
                    "Показать еще"
                  )}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostsSearch;
